










import vue from "vue";
import MarkdownIt from "markdown-it";
import FuriganaMarkdownIt from "furigana-markdown-it";
import {
  EXERCISE_TYPE,
  VUEX_EXERCISE_GETTER_NAMES,
  VUEX_EXERCISE_NAMESPACE
} from "@tt-app/constants";

import "../../../../../../public/styles/htmlText.css";

const furigana = new (FuriganaMarkdownIt as any)();

const md = new MarkdownIt({
  breaks: true
});
md.use(furigana);

interface Methods {
  calculateExerciseQuestionCardElementCount: (pages: Record<any, any>[]) => number | undefined;
  calculateFirstHtmlElementIndex: (pages: Record<any, any>[]) => number | undefined;
}

interface Computed {
  htmlFromMarkdown: string;
  templateName: string;
  writingExerciseExplanationTitle?: string;
}

interface Props {
  body: string;
  exerciseBodyIndex: number;
  elementIndex: number;
  template: number;
}

export default vue.extend<any, Methods, Computed, Props>({
  name: "ExerciseHtmlText",

  props: {
    body: {
      type: String,
      default: ""
    },
    exerciseBodyIndex: {
      type: Number,
      default: undefined
    },
    elementIndex: {
      type: Number,
      default: undefined
    },
    template: {
      type: Number,
      default: 0
    }
  },

  methods: {
    calculateExerciseQuestionCardElementCount(exercisePages) {
      const pageBody = exercisePages?.[this.exerciseBodyIndex]?.body;
      if (!pageBody) return undefined;

      const questionCard = pageBody.filter((pageDefinition: { type: string }) => {
        return pageDefinition.type === "CARD";
      });
      if (!questionCard) return undefined;

      const questionCardBody = questionCard[0].body;
      if (!questionCardBody) return undefined;

      return questionCardBody.length;
    },

    calculateFirstHtmlElementIndex(exerciseBody) {
      if (!exerciseBody) return undefined;

      const firstHtmlTextIndex = exerciseBody.findIndex(element => {
        return element.type === "HTML_TEXT";
      });

      return firstHtmlTextIndex >= 0 ? firstHtmlTextIndex : undefined;
    }
  },

  computed: {
    htmlFromMarkdown() {
      const joinedBody = this.body;

      const whitespaceInParensRegex = /(（|\()(\s+?)(）|\))/gi;
      const markdownWithReplacedBlanks = joinedBody.replaceAll(
        whitespaceInParensRegex,
        (...args: string[]) => {
          const [, leftParen, whitespaces, rightParen] = args;

          const nbspLength = Math.floor(whitespaces.length * 3.5); // two full width spaces are equal to 7 non-breaking spaces
          const nbspString = new Array(nbspLength).fill("&nbsp;").join("");

          return `${leftParen}${nbspString}${rightParen}`;
        }
      );

      return md.render(markdownWithReplacedBlanks);
    },

    templateName() {
      switch (this.template) {
        case 1:
          return "markdown-text-article";
        case 2:
          return "markdown-text-email";
        case 3:
          return "markdown-text-notice";
        case 4:
          return "markdown-text-review";
        case 5:
          return "markdown-text-word-arrange";
        default:
          return "markdown-text-simple";
      }
    },

    writingExerciseExplanationTitle() {
      const exerciseType = this.$store.getters[
        `${VUEX_EXERCISE_NAMESPACE}/${VUEX_EXERCISE_GETTER_NAMES.EXERCISE_TYPE}`
      ];
      const exercisePages = this.$store.getters[
        `${VUEX_EXERCISE_NAMESPACE}/${VUEX_EXERCISE_GETTER_NAMES.EXERCISE_PAGES}`
      ];
      const exerciseBody = this.$store.getters[
        `${VUEX_EXERCISE_NAMESPACE}/${VUEX_EXERCISE_GETTER_NAMES.BODY}`
      ];

      const questionCardElementCount = this.calculateExerciseQuestionCardElementCount(
        exercisePages
      );

      const exerciseBodyExceedingQuestion = exerciseBody?.[this.exerciseBodyIndex]?.body.slice(
        questionCardElementCount
      );

      const firstHtmlTextElementIndex = this.calculateFirstHtmlElementIndex(
        exerciseBodyExceedingQuestion
      );

      const isWritingExercise = exerciseType === EXERCISE_TYPE.WRITING;
      const isFirstHtmlText =
        questionCardElementCount + firstHtmlTextElementIndex === this.elementIndex;

      if (isWritingExercise && isFirstHtmlText) {
        return this.$__("exercise.writing.header.explanation");
      }

      return;
    }
  }
});
